import { View } from '@play-co/timestep-core/ui';
import { user } from 'src/StateObserver';
import Scroll from 'src/game/components/shared/Scroll';
import {
  FriendListItem,
  FriendListPlayerParams,
} from '../components/FriendListItem';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import i18n from 'src/lib/i18n/i18n';
import { Feature } from 'src/replicant/analytics';
import { withLoading } from 'src/lib/utils';
import { fetchFriendList, removeFriendFromCache } from '../friends.utils';
import { FriendSuggestionsSection } from '../friendSuggestions/friendSuggestions.section';
import {
  createSuggestionChromeView,
  FriendSuggestionsListItem,
} from '../friendSuggestions/friendSuggestions.items';
import { openUserProfilePopup } from '../friendsList.sequences';
import { getBuruFriendsCount } from 'src/replicant/friends/friends.getters';
import { PlayerEntryData } from 'src/replicant/friends/friends.ruleset';
import features from 'src/replicant/ruleset/features';

type TabMyFriendsOpts = {
  tab: View;
  closePopup: () => void;
};

export class TabMyFriends {
  private tab: View;
  // Cluster suggestion eligibility is fixed for this instance — read once so
  // the scroll's recycle mode and the per-refresh paint decision cannot disagree.
  private readonly suggestionsEnabled =
    features(user()).friendListClusterSuggestions;
  // Friends-only lists recycle when suggestions are unavailable. When enabled,
  // the scroll is non-recycled so the mixed-height combined list (shown when
  // the user has < 3 friends) lays out correctly.
  private scroll: Scroll<FriendSuggestionsListItem, View>;
  private noFriendsContainer: View;
  private closePopup: () => void;
  private suggestions = new FriendSuggestionsSection('friend_list', () =>
    // Preserve the scroll position when re-rendering after an Add tap.
    this.refresh({ preserveScroll: true }),
  );

  constructor(opts: TabMyFriendsOpts) {
    this.tab = opts.tab;
    this.closePopup = opts.closePopup;

    const rect = {
      x: 0,
      y: 0,
      width: this.tab.style.width,
      height: this.tab.style.height,
    };
    const createItem = (
      superview: View,
      _i: number,
      data: FriendSuggestionsListItem,
    ) => {
      const chrome = createSuggestionChromeView(superview, data);
      if (chrome) return chrome;
      const item = new FriendListItem({ superview });
      item.setup(data as FriendListPlayerParams);
      return item;
    };
    // Recycle when suggestions are unavailable; the combined list needs a
    // non-recycled scroll for its mixed-height rows.
    this.scroll = this.suggestionsEnabled
      ? new Scroll<FriendSuggestionsListItem, View>({
          superview: this.tab,
          createItem,
          showBg: true,
          rect,
        })
      : new Scroll<FriendSuggestionsListItem, View>({
          superview: this.tab,
          recycle: true,
          createItem,
          setData: (_i, data, view) =>
            (view as FriendListItem).setup(data as FriendListPlayerParams),
          showBg: true,
          rect,
        });

    // Empty state for friends list
    this.noFriendsContainer = new View({
      superview: this.tab,
      width: this.tab.style.width,
      height: this.tab.style.height,
      visible: false,
    });

    new LangBitmapFontTextView({
      superview: this.noFriendsContainer,
      align: 'center',
      verticalAlign: 'center',
      size: 28,
      font: 'Body',
      width: this.noFriendsContainer.style.width - 60,
      height: 100,
      color: '#AE7E4A',
      centerOnOrigin: true,
      x: this.noFriendsContainer.style.width / 2,
      y: this.noFriendsContainer.style.height / 2 - 50,
      localeText: () => i18n('friendsList.noFriendsInviteHint'),
    });
  }

  /** Reset per-open suggestion state. Called from the popup's init(). */
  public resetSuggestions() {
    this.suggestions.reset();
  }

  private getFriendListItemData(
    params: FriendListPlayerParams,
  ): FriendListPlayerParams {
    return {
      ...params,
      onTap: () => this.handleOnTap(params),
    };
  }

  private async handleOnTap(params: FriendListPlayerParams): Promise<void> {
    const actionTaken = await openUserProfilePopup(params.id, 'friend_list', {
      name: params.name,
      photo: params.profilePicture,
    });

    // If visit started, close this popup too
    if (actionTaken === 'visit') {
      this.closePopup();
      return;
    }

    // refresh friends list if unfriended
    if (actionTaken === 'unfriend') {
      // In this case we remove the friend from cache immediately since we know it's already been removed on the backend,
      // so we can reflect that change in the UI without needing to wait for a re-fetch
      removeFriendFromCache(params.id);
      this.refresh();
    }
  }

  /**
   * Fetch the friends list, but first validate the cache against the current `user().buruFriends` IDs.
   * If the cached IDs don't match the state (e.g. friends changed in another tab/popup), invalidate the cache
   * so we refetch fresh data.
   *
   * This is especially useful to force a refetch when a new friend is added - their ID will be in `user().buruFriends`.
   *
   * The validation also detects removed friends, but since we manually update the cache via `removeFriendFromCache`,
   * the IDs will already match and no refetch is triggered.
   */
  private async fetchFriends() {
    // Check if cache IDs match current buruFriends - if not, invalidate
    const cached = fetchFriendList().getCached();
    if (cached) {
      const cachedIds = new Set(cached.map((f) => f.id));
      const buruFriendIds = Object.keys(user().buruFriends);
      const cacheMatchesState =
        cachedIds.size === buruFriendIds.length &&
        buruFriendIds.every((id) => cachedIds.has(id));

      if (!cacheMatchesState) {
        await fetchFriendList().invalidate();
      }
    }

    return fetchFriendList().get();
  }

  public async refresh(opts: { preserveScroll?: boolean } = {}) {
    const friends = await withLoading(() => this.fetchFriends(), {
      feature: Feature.FRIENDS._,
      subFeature: Feature.FRIENDS.TAB_MY_FRIENDS,
      superview: this.tab,
      x: this.tab.style.width / 2,
      y: this.tab.style.height / 2,
      centerOnOrigin: true,
    });

    if (!friends) return;

    // Paint the friends immediately; the "You may know" section (if any) is
    // appended below afterwards. Preserve the offset on an Add-refresh.
    this.scroll.setItems(this.toFriendRows(friends), !opts.preserveScroll);
    await this.appendSuggestions(friends, opts);
  }

  /**
   * Append the "You may know" section below the friends list — only when the
   * user has < 3 friends and the test is on. The eligibility is re-checked
   * against live state AFTER the fetch: accepting a request on the Requests tab
   * can cross the user to >= 3 friends mid-flight, so a stale run must not paint
   * the section. (This live re-check replaces the old refresh generation guard.)
   */
  private async appendSuggestions(
    friends: PlayerEntryData[],
    opts: { preserveScroll?: boolean },
  ): Promise<void> {
    const eligible = () =>
      getBuruFriendsCount(user()) < 3 && this.suggestionsEnabled;

    if (!eligible()) {
      this.noFriendsContainer.updateOpts({ visible: friends.length === 0 });
      return;
    }

    // Hold the "invite friends" hint while suggestions load so it doesn't flash.
    this.noFriendsContainer.updateOpts({ visible: false });

    const { items } = await this.suggestions.fetchParams();
    const freshFriends = fetchFriendList().getCached() ?? friends;

    if (items.length === 0 || !eligible()) {
      this.noFriendsContainer.updateOpts({
        visible: freshFriends.length === 0,
      });
      return;
    }

    this.scroll.setItems(
      this.suggestions.buildRows(this.toFriendRows(freshFriends), items),
      !opts.preserveScroll,
    );
    this.noFriendsContainer.updateOpts({ visible: false });
  }

  /** Map friend entries to tile params with their tap handler baked in. */
  private toFriendRows(friends: PlayerEntryData[]): FriendListPlayerParams[] {
    return friends
      .map((friend) => ({
        id: friend.id,
        name: friend.name,
        profilePicture: friend.photo,
        frame: friend.avatarFrame,
        species: friend.petSpecies,
      }))
      .map((row) => this.getFriendListItemData(row));
  }
}
